import { CalendarPlus, ChevronDown, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { notifyMutationError } from '../lib/errors';

import {
  type CalendarEvent,
  type CalendarEventUpdate,
  createCalendarEvent,
  deleteCalendarEvent,
  updateCalendarEvent,
} from '../api/client';
import { useOfflineMutation } from '@/hooks/useOfflineMutation';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { type EventType, eventTypeColors } from '@/constants/calendar';
import { ProcessoDoAcervoCombobox } from './processos/ProcessoDoAcervoCombobox';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Textarea } from './ui/textarea';

const EVENT_TYPE_OPTIONS: { value: EventType; label: string }[] = Object.entries(
  eventTypeColors,
).map(([value, cfg]) => ({ value: value as EventType, label: cfg.label }));

const REMINDER_OPTIONS = [
  { value: '5', label: '5 minutos antes' },
  { value: '15', label: '15 minutos antes' },
  { value: '30', label: '30 minutos antes' },
  { value: '60', label: '1 hora antes' },
  { value: '1440', label: '1 dia antes' },
];

export interface EventFormDefaultValues {
  titulo?: string;
  tipo?: string;
  data_inicio?: string;
  data_fim?: string;
  processo_id?: string;
  comunicacao_id?: string;
}

interface EventFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** Evento existente — habilita modo edição + exclusão. */
  event?: CalendarEvent | null;
  /** Valores iniciais para criação (comunicação ou clique no calendário). */
  defaultValues?: EventFormDefaultValues;
}

/** Formata Date ou ISO string para input[type=datetime-local]. */
function toDatetimeLocal(value: Date | string): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Dialog wrapper: controla open/close e gera key para resetar o form interno.
 * Ao abrir, monta um EventFormContent novo (key muda), resetando todo state.
 */
export function EventFormDialog({ open, onClose, event, defaultValues }: EventFormDialogProps) {
  // Incrementa a cada abertura para forçar remount do form
  const [revision, setRevision] = useState(0);

  const handleOpenChange = (o: boolean) => {
    if (o) {
      setRevision((r) => r + 1);
    } else {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-full sm:max-w-md max-h-[90vh] overflow-y-auto">
        {open && (
          <EventFormContent
            key={`${event?.id ?? 'new'}-${revision}`}
            event={event}
            defaultValues={defaultValues}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Form interno — montado fresh a cada abertura via key. */
export function EventFormContent({
  event,
  defaultValues,
  onClose,
}: {
  event?: CalendarEvent | null;
  defaultValues?: EventFormDefaultValues;
  onClose: () => void;
}) {
  const isEditing = !!event;

  // Valores iniciais derivados de event ou defaultValues (calculados uma vez no mount)
  const [titulo, setTitulo] = useState(() =>
    event ? event.titulo : (defaultValues?.titulo || ''),
  );
  // O evento novo nasce prazo: é o que o advogado mais lança à mão.
  const tipoInicial: EventType = event
    ? ((event.tipo as EventType) || 'outro')
    : ((defaultValues?.tipo as EventType) || 'prazo');
  const [tipo, setTipo] = useState<EventType>(tipoInicial);
  const [dataInicio, setDataInicio] = useState(() => {
    if (event) return toDatetimeLocal(event.data_inicio);
    if (!defaultValues?.data_inicio) return '';
    const inicio = toDatetimeLocal(defaultValues.data_inicio);
    // Prazo vence no fim do dia (como ao trocar o tipo para prazo).
    return tipoInicial === 'prazo' && inicio ? `${inicio.slice(0, 10)}T23:59` : inicio;
  });
  const [dataFim, setDataFim] = useState(() =>
    event?.data_fim
      ? toDatetimeLocal(event.data_fim)
      : defaultValues?.data_fim
        ? toDatetimeLocal(defaultValues.data_fim)
        : '',
  );
  const [processoId, setProcessoId] = useState<string | null>(() =>
    event ? (event.processo_id ?? null) : (defaultValues?.processo_id ?? null),
  );
  // O processo do evento que nasceu de uma intimação vem dela: a ingestão do DJEN
  // deduplica e funde as audiências pelo processo, e trocá-lo duplicaria a agenda.
  const processoEscolhivel =
    !(event?.comunicacao_id || defaultValues?.comunicacao_id) && (!event?.origem || event.origem === 'usuario');
  const [local, setLocal] = useState(() => event?.local || '');
  const [descricao, setDescricao] = useState(() => event?.descricao || '');
  const [lembreteMinutos, setLembreteMinutos] = useState(() =>
    String(event?.lembrete_minutos ?? localStorage.getItem('evento_lembrete_minutos') ?? 30),
  );
  const [showMore, setShowMore] = useState(() =>
    isEditing || !defaultValues?.titulo,
  );
  const [confirmDelete, setConfirmDelete] = useState(false);

  const createMutation = useOfflineMutation({
    mutationFn: createCalendarEvent,
    outbox: {
      type: 'evento_criar',
      getPayload: (vars) => ({ method: 'POST', url: '/calendario/eventos', body: vars }),
      getDescription: (vars) => vars.titulo,
    },
    invalidateKeys: [[...queryKeys.calendario.eventos], [...queryKeys.calendario.agenda], [...queryKeys.comunicacoes.all], [...queryKeys.processos.all], [...queryKeys.dashboard.regua]],
    onSuccess: () => {
      toast.success('Evento criado com sucesso');
      onClose();
    },
    onQueued: () => {
      onClose();
    },
    onError: (err) => notifyMutationError(err, toast.error),
  });

  const updateMutation = useOfflineMutation<CalendarEvent, { id: string; data: CalendarEventUpdate }>({
    mutationFn: ({ id, data }) => updateCalendarEvent(id, data),
    outbox: {
      type: 'evento_editar',
      getPayload: (vars) => ({ method: 'PUT', url: `/calendario/eventos/${vars.id}`, body: vars.data }),
      getDescription: (vars) => vars.data.titulo ?? 'Evento',
    },
    invalidateKeys: [[...queryKeys.calendario.eventos], [...queryKeys.calendario.agenda], [...queryKeys.comunicacoes.all], [...queryKeys.processos.all], [...queryKeys.dashboard.regua]],
    onSuccess: () => {
      toast.success('Evento atualizado com sucesso');
      onClose();
    },
    onQueued: () => {
      onClose();
    },
    onError: (err) => notifyMutationError(err, toast.error),
  });

  const deleteMutation = useOfflineMutation<void, string>({
    mutationFn: deleteCalendarEvent,
    outbox: {
      type: 'evento_deletar',
      getPayload: (id) => ({ method: 'DELETE', url: `/calendario/eventos/${id}` }),
      getDescription: () => 'Excluir evento',
    },
    invalidateKeys: [[...queryKeys.calendario.eventos], [...queryKeys.calendario.agenda], [...queryKeys.comunicacoes.all], [...queryKeys.processos.all], [...queryKeys.dashboard.regua]],
    onSuccess: () => {
      toast.success('Evento excluído');
      onClose();
    },
    onQueued: () => {
      onClose();
    },
    onError: (err) => notifyMutationError(err, toast.error),
  });

  const isPending = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  // Prazo: o que importa é a data final. A hora é fixada em 23:59 (fim do dia) e o
  // término não se aplica — então o campo de data vira "somente data".
  const isPrazo = tipo === 'prazo';

  const handleSubmit = () => {
    if (!titulo.trim() || !dataInicio) return;

    const payload = {
      titulo: titulo.trim(),
      tipo,
      data_inicio: new Date(dataInicio).toISOString(),
      data_fim: !isPrazo && dataFim ? new Date(dataFim).toISOString() : undefined,
      local: local.trim() || undefined,
      descricao: descricao.trim() || undefined,
      lembrete_minutos: Number(lembreteMinutos),
      processo_id: processoId || undefined,
      comunicacao_id: defaultValues?.comunicacao_id || event?.comunicacao_id || undefined,
    };

    if (isEditing && event) {
      // `null` explícito desfaz o vínculo; ausente, o servidor mantém o que há.
      const desvincular = !processoId && !!event.processo_id;
      updateMutation.mutate({ id: event.id, data: { ...payload, ...(desvincular && { processo_id: null }) } });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDelete = () => {
    if (!event) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    deleteMutation.mutate(event.id);
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <CalendarPlus className="h-5 w-5" />
          {isEditing ? 'Editar Evento' : 'Novo Evento'}
        </DialogTitle>
        <DialogDescription>
          {isEditing
            ? 'Altere os dados do evento.'
            : 'Preencha os dados para criar um novo evento.'}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-2">
        <div className="space-y-2">
          <Label htmlFor="ef-titulo">Título *</Label>
          <Input
            id="ef-titulo"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Ex: Sessão de instrução"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="ef-tipo">Tipo</Label>
          <Select
            value={tipo}
            onValueChange={(v) => {
              const next = v as EventType;
              setTipo(next);
              // Ao virar prazo, fixa a hora em 23:59 (fim do dia da data já escolhida).
              if (next === 'prazo' && dataInicio) {
                setDataInicio(`${dataInicio.slice(0, 10)}T23:59`);
              }
            }}
          >
            <SelectTrigger id="ef-tipo">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EVENT_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="ef-inicio">{isPrazo ? 'Data final do prazo *' : 'Data e Hora *'}</Label>
          {isPrazo ? (
            <Input
              id="ef-inicio"
              type="date"
              value={dataInicio.slice(0, 10)}
              onChange={(e) => setDataInicio(e.target.value ? `${e.target.value}T23:59` : '')}
            />
          ) : (
            <Input
              id="ef-inicio"
              type="datetime-local"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
            />
          )}
        </div>

        {processoEscolhivel && (
          <div className="space-y-2">
            <Label htmlFor="ef-processo">Processo</Label>
            <ProcessoDoAcervoCombobox
              id="ef-processo"
              value={processoId}
              onChange={setProcessoId}
              numeroAtual={event?.processo_numero}
            />
          </div>
        )}

        {!showMore && (
          <button
            type="button"
            onClick={() => setShowMore(true)}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronDown className="h-4 w-4" />
            Mais opções
          </button>
        )}

        {showMore && (
          <div className="space-y-4 border-t border-border pt-4">
            {!isPrazo && (
              <div className="space-y-2">
                <Label htmlFor="ef-fim">Data e Hora de Término</Label>
                <Input
                  id="ef-fim"
                  type="datetime-local"
                  value={dataFim}
                  onChange={(e) => setDataFim(e.target.value)}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="ef-local">Local</Label>
              <Input
                id="ef-local"
                value={local}
                onChange={(e) => setLocal(e.target.value)}
                placeholder="Ex: Sala 3, Fórum Central"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ef-descricao">Descrição</Label>
              <Textarea
                id="ef-descricao"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Observações sobre o evento"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ef-lembrete">Lembrete</Label>
              <Select value={lembreteMinutos} onValueChange={(v) => { setLembreteMinutos(v); localStorage.setItem('evento_lembrete_minutos', v); }}>
                <SelectTrigger id="ef-lembrete">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REMINDER_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      <DialogFooter className="flex-col gap-2 sm:flex-row">
        {isEditing && (
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isPending}
            className="mr-auto"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {confirmDelete ? 'Confirmar exclusão' : 'Excluir'}
          </Button>
        )}
        <Button variant="outline" onClick={onClose} disabled={isPending}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isPending || !titulo.trim() || !dataInicio}
        >
          {isPending ? 'Salvando...' : isEditing ? 'Salvar' : 'Criar'}
        </Button>
      </DialogFooter>
    </>
  );
}
